import React, { useState, useEffect } from 'react';
import Icon from './Icon';

// ==================== BUTTON ====================
export function Button({ children, onClick, variant = 'primary', size = 'md', icon, disabled, style: s2 }) {
  const base = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontWeight: 600, borderRadius: size === 'sm' ? 8 : 12, transition: 'all .2s', opacity: disabled ? 0.5 : 1 };
  const sizes = { sm: { padding: '7px 14px', fontSize: 12 }, md: { padding: '12px 22px', fontSize: 14 }, lg: { padding: '14px 28px', fontSize: 15 } };
  const variants = {
    primary: { background: 'linear-gradient(135deg, var(--accent), var(--accent-dark))', color: '#fff', boxShadow: '0 6px 20px rgba(99,102,241,0.3)' },
    secondary: { background: 'var(--accent-light)', color: 'var(--accent)', boxShadow: 'none' },
    outline: { background: 'transparent', color: 'var(--accent)', border: '1.5px solid var(--accent)', boxShadow: 'none' },
    danger: { background: '#fee2e2', color: 'var(--red)', boxShadow: 'none' },
    ghost: { background: 'transparent', color: 'var(--text-muted)', boxShadow: 'none' },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{ ...base, ...sizes[size], ...variants[variant], ...s2 }}>
      {icon && <Icon name={icon} size={size === 'sm' ? 14 : 16} />}{children}
    </button>
  );
}

// ==================== INPUT ====================
export function Input({ label, value, onChange, type = 'text', placeholder, required, icon, disabled }) {
  const [f, setF] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword ? (showPw ? 'text' : 'password') : type;

  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>{label}{required && <span style={{ color: 'var(--red)' }}> *</span>}</label>}
      <div style={{ position: 'relative' }}>
        <input type={inputType} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled}
          onFocus={() => setF(true)} onBlur={() => setF(false)}
          style={{ width: '100%', padding: icon ? '13px 16px 13px 40px' : isPassword ? '13px 44px 13px 16px' : '13px 16px', borderRadius: 12, border: `2px solid ${f ? 'var(--accent)' : 'var(--border)'}`, background: disabled ? '#f1f5f9' : 'var(--bg)', color: 'var(--text)', fontSize: 14, fontFamily: 'inherit', transition: 'all .2s', boxShadow: f ? '0 0 0 4px var(--accent-soft)' : 'none' }} />
        {icon && <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }}><Icon name={icon} size={16} /></div>}
        {isPassword && (
          <button type="button" onClick={() => setShowPw(!showPw)}
            style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 4, display: 'flex', alignItems: 'center' }}>
            <Icon name={showPw ? 'eyeOff' : 'eye'} size={18} />
          </button>
        )}
      </div>
    </div>
  );
}

// ==================== PHONE INPUT ====================
export function PhoneInput({ label, value, onChange, required }) {
  const [f, setF] = useState(false);

  const formatDisplay = (raw) => {
    let digits = (raw || '').replace(/\D/g, '');
    if (digits.startsWith('998')) digits = digits.slice(3);
    digits = digits.slice(0, 9);
    let out = '';
    if (digits.length > 0) out += digits.slice(0, 2);
    if (digits.length > 2) out += ' ' + digits.slice(2, 5);
    if (digits.length > 5) out += ' ' + digits.slice(5, 7);
    if (digits.length > 7) out += ' ' + digits.slice(7, 9);
    return out;
  };

  const handleChange = (e) => {
    const after = e.target.value;
    const digits = after.replace(/\D/g, '').slice(0, 9);
    onChange(digits ? '+998' + digits : '');
  };

  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>{label}{required && <span style={{ color: 'var(--red)' }}> *</span>}</label>}
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 14, fontWeight: 500, color: 'var(--text)', pointerEvents: 'none', fontFamily: "'Fira Code', monospace" }}>+998</div>
        <input type="tel" value={formatDisplay(value)} onChange={handleChange} placeholder="XX XXX XX XX"
          onFocus={() => setF(true)} onBlur={() => setF(false)}
          style={{ width: '100%', padding: '13px 16px 13px 58px', borderRadius: 12, border: `2px solid ${f ? 'var(--accent)' : 'var(--border)'}`, background: 'var(--bg)', color: 'var(--text)', fontSize: 14, fontFamily: "'Fira Code', monospace", transition: 'all .2s', boxShadow: f ? '0 0 0 4px var(--accent-soft)' : 'none' }} />
      </div>
    </div>
  );
}

// ==================== MASKED INPUT ====================
export function MaskedInput({ label, value, onChange, mask, placeholder, required, disabled }) {
  // mask types: 'jshshir' (14 digits), 'passport_series' (2 uppercase letters), 
  // 'passport_number' (7 digits), 'stir' (9 digits), 'digits' (any length digits)
  const [f, setF] = useState(false);

  const masks = {
    jshshir: {
      maxLen: 14,
      filter: v => v.replace(/\D/g, '').slice(0, 14),
      format: v => {
        const d = v.replace(/\D/g, '').slice(0, 14);
        // Format: X XXXX XXXX XXXXX
        let out = '';
        if (d.length > 0) out += d.slice(0, 1);
        if (d.length > 1) out += ' ' + d.slice(1, 5);
        if (d.length > 5) out += ' ' + d.slice(5, 9);
        if (d.length > 9) out += ' ' + d.slice(9, 14);
        return out;
      },
      raw: v => v.replace(/\D/g, '').slice(0, 14),
      ph: 'X XXXX XXXX XXXXX',
      counter: v => `${v.replace(/\D/g, '').length}/14`,
    },
    passport_series: {
      maxLen: 2,
      filter: v => v.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 2),
      format: v => v.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 2),
      raw: v => v.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 2),
      ph: 'AA',
      counter: v => `${v.replace(/[^A-Za-z]/g, '').length}/2`,
    },
    passport_number: {
      maxLen: 7,
      filter: v => v.replace(/\D/g, '').slice(0, 7),
      format: v => v.replace(/\D/g, '').slice(0, 7),
      raw: v => v.replace(/\D/g, '').slice(0, 7),
      ph: '1234567',
      counter: v => `${v.replace(/\D/g, '').length}/7`,
    },
    stir: {
      maxLen: 9,
      filter: v => v.replace(/\D/g, '').slice(0, 9),
      format: v => {
        const d = v.replace(/\D/g, '').slice(0, 9);
        let out = '';
        if (d.length > 0) out += d.slice(0, 3);
        if (d.length > 3) out += ' ' + d.slice(3, 6);
        if (d.length > 6) out += ' ' + d.slice(6, 9);
        return out;
      },
      raw: v => v.replace(/\D/g, '').slice(0, 9),
      ph: 'XXX XXX XXX',
      counter: v => `${v.replace(/\D/g, '').length}/9`,
    },
    diploma: {
      maxLen: 10,
      filter: v => v.toUpperCase().slice(0, 10),
      format: v => {
        let clean = v.toUpperCase();
        const letters = clean.replace(/[^A-Za-z]/g, '').slice(0, 2);
        const digits = clean.replace(/\D/g, '').slice(0, 7);
        if (!letters && !digits) return '';
        return letters + (digits ? ' ' + digits : '');
      },
      raw: v => v.toUpperCase().replace(/\s+/g, '').slice(0, 9),
      ph: 'DK 1234567',
      counter: null,
    },
  };

  const m = masks[mask] || { format: v => v, raw: v => v, ph: placeholder || '', counter: null };

  const handleChange = (e) => {
    const raw = m.raw(e.target.value);
    onChange(raw);
  };

  const displayValue = m.format(value || '');
  const counterText = m.counter ? m.counter(value || '') : null;
  const isComplete = mask === 'jshshir' ? (value || '').replace(/\D/g, '').length === 14
    : mask === 'passport_series' ? (value || '').length === 2
    : mask === 'passport_number' ? (value || '').replace(/\D/g, '').length === 7
    : mask === 'stir' ? (value || '').replace(/\D/g, '').length === 9
    : false;

  return (
    <div style={{ marginBottom: 16 }}>
      {label && (
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span>{label}{required && <span style={{ color: 'var(--red)' }}> *</span>}</span>
          {counterText && (
            <span style={{ fontSize: 11, fontWeight: 600, fontFamily: "'Fira Code', monospace", color: isComplete ? 'var(--green)' : 'var(--text-dim)' }}>
              {counterText} {isComplete && '✓'}
            </span>
          )}
        </label>
      )}
      <input
        type="text"
        value={displayValue}
        onChange={handleChange}
        placeholder={m.ph || placeholder}
        disabled={disabled}
        onFocus={() => setF(true)}
        onBlur={() => setF(false)}
        style={{
          width: '100%', padding: '13px 16px', borderRadius: 12,
          border: `2px solid ${isComplete ? 'var(--green)' : f ? 'var(--accent)' : 'var(--border)'}`,
          background: disabled ? '#f1f5f9' : 'var(--bg)', color: 'var(--text)',
          fontSize: 14, fontFamily: mask === 'passport_series' ? 'inherit' : "'Fira Code', monospace",
          transition: 'all .2s', letterSpacing: mask === 'passport_series' ? '2px' : '0.5px',
          boxShadow: isComplete ? '0 0 0 4px rgba(34,197,94,0.08)' : f ? '0 0 0 4px var(--accent-soft)' : 'none',
          textTransform: mask === 'passport_series' ? 'uppercase' : 'none',
        }}
      />
    </div>
  );
}

// ==================== SELECT ====================
export function Select({ label, value, onChange, options, required }) {
  const [f, setF] = useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>{label}{required && <span style={{ color: 'var(--red)' }}> *</span>}</label>}
      <select value={value} onChange={e => onChange(e.target.value)} onFocus={() => setF(true)} onBlur={() => setF(false)}
        style={{ width: '100%', padding: '13px 16px', borderRadius: 12, border: `2px solid ${f ? 'var(--accent)' : 'var(--border)'}`, background: 'var(--bg)', color: 'var(--text)', fontSize: 14, fontFamily: 'inherit', transition: 'all .2s', appearance: 'none', cursor: 'pointer' }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// ==================== MODAL ====================
export function Modal({ open, onClose, title, children, width = 500 }) {
  if (!open) return null;
  return (
    <div className="fade-in" onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(30,27,75,0.4)', backdropFilter: 'blur(8px)' }}>
      <div className="fade-up" onClick={e => e.stopPropagation()} style={{ width, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', borderRadius: 20, background: '#fff', boxShadow: '0 24px 80px rgba(99,102,241,0.15)', padding: '28px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 4 }}><Icon name="x" size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ==================== TOAST ====================
export function Toast({ message, type = 'success', onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className="fade-up" style={{ position: 'fixed', top: 24, right: 24, zIndex: 2000, padding: '14px 24px', borderRadius: 14, background: type === 'success' ? '#dcfce7' : '#fee2e2', color: type === 'success' ? 'var(--green)' : 'var(--red)', fontSize: 14, fontWeight: 600, boxShadow: '0 8px 32px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: 10 }}>
      <Icon name={type === 'success' ? 'check' : 'x'} size={18} /> {message}
    </div>
  );
}
